<template>
  <el-dialog
    title="批量衍生"
    :visible.sync="visibleProxy"
    width="640px"
    append-to-body
    custom-class="beauty-dialog batch-derive-dialog"
    @open="handleOpen"
    @close="handleClose"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="120px" size="small" v-loading="optionsLoading">
      <el-form-item label="模板" prop="templateId">
        <el-select v-model="form.templateId" clearable placeholder="不使用模板" style="width: 100%" @change="onTemplateChange">
          <el-option label="不使用模板" :value="null" />
          <el-option
            v-for="t in templates"
            :key="t.id"
            :label="t.name"
            :value="t.id"
          />
        </el-select>
        <div class="form-tip">模板只用于预填参数，不会覆盖你最终提交的内容</div>
      </el-form-item>

      <el-form-item label="模型" prop="model" required>
        <el-select v-model="form.model" placeholder="请选择模型" style="width: 100%" @change="onModelChange">
          <el-option
            v-for="m in models"
            :key="m.id"
            :label="m.name || m.id"
            :value="m.id"
            :disabled="m.supportsReference === false"
          >
            <span>{{ m.name || m.id }}</span>
            <span v-if="m.supportsReference === false" style="color:#999;margin-left:8px;font-size:12px">不支持参考图</span>
          </el-option>
        </el-select>
        <div class="form-tip">模型来自公司 UShot 平台配置，支持多模型</div>
      </el-form-item>

      <el-form-item label="提示词" prop="prompt" required>
        <el-input
          v-model="form.prompt"
          type="textarea"
          :rows="5"
          maxlength="2000"
          show-word-limit
          placeholder="请输入图片提示词，说明希望如何基于源图衍生"
        />
      </el-form-item>

      <el-form-item label="尺寸" prop="sizeKey" required>
        <el-select v-model="form.sizeKey" placeholder="请选择尺寸" style="width: 100%">
          <el-option
            v-for="opt in sizeOptions"
            :key="opt.key"
            :label="opt.label"
            :value="opt.key"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="每张生成数量" prop="nPerSource" required>
        <el-input-number v-model="form.nPerSource" :min="1" :max="4" controls-position="right" />
      </el-form-item>

      <el-form-item label="命名模式" prop="namingMode">
        <el-radio-group v-model="form.namingMode">
          <el-radio label="derive">衍生命名</el-radio>
          <el-radio label="sequential">顺序编号</el-radio>
        </el-radio-group>
        <div class="form-tip" v-if="form.namingMode === 'derive'">按下方规则命名，不使用源素材名称</div>
        <div class="form-tip" v-else>按 AI-日期-序号 自动命名</div>
      </el-form-item>

      <el-form-item label="输出目录" prop="outputFolderId">
        <el-cascader
          v-model="folderPath"
          :options="folderCascaderOptions"
          :props="{ checkStrictly: true, value: 'id', label: 'name', children: 'children', emitPath: true }"
          clearable
          filterable
          placeholder="全部素材（默认）"
          style="width: 100%"
          @change="onFolderChange"
        />
      </el-form-item>

      <el-form-item v-if="form.namingMode === 'derive'" label="衍生名称规则" prop="deriveNameRule">
        <el-input
          v-model="form.deriveNameRule"
          maxlength="1990"
          show-word-limit
          placeholder="例如：YYMMDD-衍生"
        />
        <div class="form-tip">这条规则就是新素材名，不再带源素材名称。YYMMDD 会换成日期；一次生成多张时自动加序号</div>
      </el-form-item>

      <el-form-item label="自动打标签" prop="tagIds">
        <el-select v-model="form.tagIds" multiple filterable collapse-tags placeholder="选择标签（可选）" style="width: 100%">
          <el-option
            v-for="tag in tagOptions"
            :key="tag.id"
            :label="tag.name"
            :value="tag.id"
          >
            <span :style="{ color: tag.color }">●</span>
            <span style="margin-left: 8px">{{ tag.name }}</span>
          </el-option>
        </el-select>
      </el-form-item>
    </el-form>

    <div class="info-bar">
      <i class="el-icon-info"></i>
      已选 {{ imageCount }} 张图片素材，共预计生成 {{ expectedCount }} 张
      <span v-if="!options.enabled" class="warn-text">（当前未配置 UShot，无法提交）</span>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="visibleProxy = false">取消</el-button>
      <el-button type="primary" :loading="submitting" :disabled="!canSubmit" @click="handleSubmit">提交生成</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  getAiGenerateOptions,
  createAiDeriveBatch,
  listAllAiPromptTemplates
} from '@/api/material/aiGenerate';

const DEFAULT_SIZES = [
  { key: '1k|1:1', label: '1K - 1:1', resolution: '1k', aspectRatio: '1:1' },
  { key: '1k|3:4', label: '1K - 3:4', resolution: '1k', aspectRatio: '3:4' },
  { key: '1k|4:3', label: '1K - 4:3', resolution: '1k', aspectRatio: '4:3' },
  { key: '1k|16:9', label: '1K - 16:9', resolution: '1k', aspectRatio: '16:9' },
  { key: '1k|9:16', label: '1K - 9:16', resolution: '1k', aspectRatio: '9:16' },
  { key: '2k|1:1', label: '2K - 1:1', resolution: '2k', aspectRatio: '1:1' },
  { key: '2k|3:4', label: '2K - 3:4', resolution: '2k', aspectRatio: '3:4' },
  { key: '2k|4:3', label: '2K - 4:3', resolution: '2k', aspectRatio: '4:3' },
  { key: '2k|16:9', label: '2K - 16:9', resolution: '2k', aspectRatio: '16:9' },
  { key: '2k|9:16', label: '2K - 9:16', resolution: '2k', aspectRatio: '9:16' }
];

/** 下拉展示顺序：豆包优先，默认选中 5.0 Lite */
const PREFERRED_MODEL_ORDER = [
  'doubao-seedream-5.0-lite',
  'doubao-seedream-4.5',
  'doubao-seedream-4.0',
  'google-nano-banana-2'
];
const DEFAULT_DERIVE_MODEL = 'doubao-seedream-5.0-lite';

export default {
  name: 'BatchDeriveDialog',
  props: {
    visible: { type: Boolean, default: false },
    materials: { type: Array, default: () => [] },
    tagOptions: { type: Array, default: () => [] },
    folderTree: { type: Array, default: () => [] }
  },
  data() {
    return {
      optionsLoading: false,
      submitting: false,
      options: { enabled: false, models: [], defaultModel: '', maxPerBatch: 100 },
      templates: [],
      folderPath: [],
      form: {
        templateId: null,
        model: '',
        prompt: '',
        sizeKey: '2k|1:1',
        nPerSource: 1,
        namingMode: 'derive',
        deriveNameRule: 'YYMMDD-衍生',
        outputFolderId: null,
        tagIds: []
      },
      rules: {
        model: [{ required: true, message: '请选择模型', trigger: 'change' }],
        prompt: [{ required: true, message: '请填写提示词', trigger: 'blur' }],
        sizeKey: [{ required: true, message: '请选择尺寸', trigger: 'change' }],
        nPerSource: [{ required: true, message: '请填写数量', trigger: 'change' }],
        deriveNameRule: [{
          validator: (rule, value, callback) => {
            if (this.form.namingMode === 'derive' && (!value || !String(value).trim())) {
              callback(new Error('请填写衍生名称规则'));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }]
      }
    };
  },
  computed: {
    visibleProxy: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    imageMaterials() {
      return (this.materials || []).filter(m => m && m.type === 'image');
    },
    imageCount() {
      return this.imageMaterials.length;
    },
    expectedCount() {
      return this.imageCount * (Number(this.form.nPerSource) || 1);
    },
    models() {
      const list = Array.isArray(this.options.models) ? [...this.options.models] : [];
      const rank = (id) => {
        const i = PREFERRED_MODEL_ORDER.indexOf(id);
        return i === -1 ? PREFERRED_MODEL_ORDER.length : i;
      };
      list.sort((a, b) => rank(a && a.id) - rank(b && b.id));
      return list;
    },
    currentModel() {
      return this.models.find(m => m.id === this.form.model) || null;
    },
    sizeOptions() {
      const m = this.currentModel;
      if (!m) return DEFAULT_SIZES;
      const resolutions = m.resolutions || ['1k', '2k'];
      const ratios = m.aspectRatios || ['1:1', '3:4', '4:3', '16:9', '9:16'];
      const list = [];
      resolutions.forEach(r => {
        ratios.forEach(a => {
          const resLabel = String(r);
          list.push({
            key: `${r}|${a}`,
            label: `${resLabel} - ${a}`,
            resolution: r,
            aspectRatio: a
          });
        });
      });
      return list.length ? list : DEFAULT_SIZES;
    },
    folderCascaderOptions() {
      const root = (this.folderTree && this.folderTree[0] && this.folderTree[0].children) || this.folderTree || [];
      return this.normalizeFolderOptions(root);
    },
    canSubmit() {
      return this.options.enabled && this.imageCount > 0 && this.expectedCount > 0
        && this.expectedCount <= (this.options.maxPerBatch || 100);
    }
  },
  methods: {
    normalizeFolderOptions(nodes) {
      if (!Array.isArray(nodes)) return [];
      return nodes.map(n => {
        const item = { id: n.id, name: n.name };
        if (n.children && n.children.length) {
          item.children = this.normalizeFolderOptions(n.children);
        }
        return item;
      });
    },
    async handleOpen() {
      this.resetForm();
      await Promise.all([this.loadOptions(), this.loadTemplates()]);
    },
    handleClose() {
      this.submitting = false;
    },
    resetForm() {
      this.folderPath = [];
      this.form = {
        templateId: null,
        model: '',
        prompt: '',
        sizeKey: '2k|1:1',
        nPerSource: 1,
        namingMode: 'derive',
        deriveNameRule: 'YYMMDD-衍生',
        outputFolderId: null,
        tagIds: []
      };
    },
    async loadOptions() {
      this.optionsLoading = true;
      try {
        const res = await getAiGenerateOptions();
        this.options = res.data || res || {};
        if (!this.form.model) {
          const hasDefault = this.models.some(m => m && m.id === DEFAULT_DERIVE_MODEL);
          this.form.model = hasDefault
            ? DEFAULT_DERIVE_MODEL
            : (this.options.defaultModel || (this.models[0] && this.models[0].id) || '');
        }
        this.onModelChange();
      } catch (e) {
        this.options = { enabled: false, models: [], maxPerBatch: 100 };
      } finally {
        this.optionsLoading = false;
      }
    },
    async loadTemplates() {
      try {
        const res = await listAllAiPromptTemplates();
        this.templates = res.data || [];
      } catch (e) {
        this.templates = [];
      }
    },
    onModelChange() {
      const keys = this.sizeOptions.map(s => s.key);
      if (!keys.includes(this.form.sizeKey)) {
        this.form.sizeKey = keys[0] || '1k|1:1';
      }
    },
    onTemplateChange(id) {
      if (!id) return;
      const t = this.templates.find(x => x.id === id);
      if (!t) return;
      if (t.prompt) this.form.prompt = t.prompt;
      if (t.model) this.form.model = t.model;
      if (t.resolution && t.aspectRatio) {
        this.form.sizeKey = `${t.resolution}|${t.aspectRatio}`;
      }
      if (t.nPerSource) this.form.nPerSource = t.nPerSource;
      if (t.namingMode) this.form.namingMode = t.namingMode;
      if (t.deriveNameRule) this.form.deriveNameRule = t.deriveNameRule;
      if (t.tagIdsJson) {
        try {
          const ids = JSON.parse(t.tagIdsJson);
          if (Array.isArray(ids)) this.form.tagIds = ids;
        } catch (e) { /* ignore */ }
      }
      this.onModelChange();
    },
    onFolderChange(path) {
      if (!path || !path.length) {
        this.form.outputFolderId = null;
        return;
      }
      this.form.outputFolderId = path[path.length - 1];
    },
    parseSize() {
      const opt = this.sizeOptions.find(s => s.key === this.form.sizeKey) || DEFAULT_SIZES[0];
      return { resolution: opt.resolution, aspectRatio: opt.aspectRatio };
    },
    handleSubmit() {
      this.$refs.form.validate(async (ok) => {
        if (!ok) return;
        if (this.imageCount === 0) {
          this.$message.warning('请先勾选图片素材');
          return;
        }
        if (this.expectedCount > (this.options.maxPerBatch || 100)) {
          this.$message.warning(`预计生成 ${this.expectedCount} 张，超过单批上限 ${this.options.maxPerBatch || 100}`);
          return;
        }
        const size = this.parseSize();
        this.submitting = true;
        try {
          const payload = {
            materialIds: this.imageMaterials.map(m => m.id),
            model: this.form.model,
            prompt: this.form.prompt,
            resolution: size.resolution,
            aspectRatio: size.aspectRatio,
            nPerSource: Number(this.form.nPerSource) || 1,
            namingMode: this.form.namingMode,
            deriveNameRule: this.form.deriveNameRule,
            tagIds: Array.isArray(this.form.tagIds) ? this.form.tagIds : []
          };
          // 可选字段：有值再传，避免 null 干扰部分网关/解析器
          if (this.form.templateId) {
            payload.templateId = this.form.templateId;
          }
          if (this.form.outputFolderId) {
            payload.outputFolderId = this.form.outputFolderId;
          }
          const res = await createAiDeriveBatch(payload);
          const batch = res.data || res;
          this.$message.success(`已提交生成任务 #${batch.id || ''}，预计 ${this.expectedCount} 张`);
          this.visibleProxy = false;
          this.$emit('success', batch);
        } catch (e) {
          // 全局拦截器已提示
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.info-bar {
  margin-top: 4px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  color: #606266;
  font-size: 13px;
}
.info-bar i {
  margin-right: 6px;
  color: #409eff;
}
.warn-text {
  color: #e6a23c;
  margin-left: 6px;
}
</style>
